import React from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useTheme } from '@mui/material/styles'
import Container from '@mui/material/Container'
import Stack from '@mui/material/Stack'
import CircularProgress from '@mui/material/CircularProgress'
import Pagination from '@mui/material/Pagination'
import PaginationItem from '@mui/material/PaginationItem'
import TextField from '@mui/material/TextField'
import Badge from '@mui/material/Badge'
import Typography from '@mui/material/Typography'
import LoadingButton from '@mui/lab/LoadingButton'
import Button from '@mui/material/Button'
import PublishOutlinedIcon from '@mui/icons-material/PublishOutlined'
import CheckIcon from '@mui/icons-material/Check'
import useMediaQuery from '@mui/material/useMediaQuery'

import { useContextHook } from '../store/context'
import { getAnonymous, postAnonymous, setPageTitle } from '../utils/service'
import Alert from '../components/alert/alert'
import { getCuppingSampleLabel, calculateSampleScore } from '../utils/cuppingUtils'
import { CuppingSession } from '../types/cuppingSession'
import { CuppingSessionStatus } from '../types/enums/cuppingSessionStatus'
import CuppingScoreForm from '../components/cupping/cuppingScoreForm'
import { CuppingSessionResult, CuppingSessionResultSample } from '../types/cuppingResult'
import { CuppingDescriptor } from '../types/cuppingFormConfig'
import SummaryTable from '../components/cupping/summaryTable'
import {
  getCompletedCuppingSessions,
  getTemporaryCuppingScores,
  setCompletedCuppingSessions,
  setTemporaryCuppingScores,
} from '../utils/localStorageUtils'
import Results from '../components/cupping/results'
import CuppingSampleHeader from '../components/cupping/cuppingSampleHeader'

const SUBMIT_PAGE_INDEX = -1

const RegisterCuppingView = (): JSX.Element => {
  const { t } = useTranslation()
  const params = useParams()
  const theme = useTheme()
  const isScreenBelowMdBreakpoint = useMediaQuery(theme.breakpoints.down('md'))
  const { state } = useContextHook()

  const [evaluatorName, setEvaluatorName] = React.useState<string>('')
  const [currentCuppingScoreIndex, setCurrentCuppingScoreIndex] = React.useState<number>(0)
  const [cuppingScores, setCuppingScores] = React.useState<Array<CuppingSessionResultSample>>([])
  const [cuppingSessionData, setCuppingSessionData] = React.useState<CuppingSession | null>(null)
  const [descriptors, setDescriptors] = React.useState<Array<CuppingDescriptor>>([])
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [isSaving, setIsSaving] = React.useState<boolean>(false)
  const [loadError, setLoadError] = React.useState<string>('')
  const [hasSubmitError, setHasSubmitError] = React.useState<boolean>(false)
  const [hasFormChanges, setHasFormChanges] = React.useState<boolean>(false)

  React.useEffect(() => {
    setEvaluatorName(state.user?.first_name || '')
  }, [state.user?.first_name])

  React.useEffect(() => {
    if (params.cuppingGuid) {
      setIsLoading(true)
      // load cupping + existing scores (if any)
      getAnonymous<CuppingSession>(`cuppingsessions/${params.cuppingGuid}/sessionconfig/`)
        .then((response) => {
          const cuppingSession = response.data
          if (cuppingSession.status === CuppingSessionStatus.Done) {
            setLoadError(t('cupping.sessionisclosed'))
          }
          setIsLoading(false)
          setPageTitle(`${t('cupping.cupping')} ${cuppingSession.name}`)
          setCuppingSessionData(cuppingSession)
          // set all cupping scores for all samples to 0, or load from localStorage
          const temporaryScores = getTemporaryCuppingScores()
          if (temporaryScores?.cupping_session === cuppingSession.url) {
            setCuppingScores(temporaryScores.sample_scores)
          } else {
            setCuppingScores(
              cuppingSession.cupping_samples.map((sample) => {
                return {
                  cupping_session_sample: sample.id || 0,
                  descriptors: [],
                  notes: '',
                  total_score: 0,
                  scores: cuppingSession.cupping_form_data.form_fields.map((field) => {
                    // here we expect all slider fields to have a default value set
                    const defaultValue = field.field_values.find((x) => x.value === field.default_value)
                    return {
                      cupping_form_field: field.id || 0,
                      score: defaultValue ? defaultValue.value : undefined,
                    }
                  }),
                }
              })
            )
          }

          if (cuppingSession.cupping_form_data.enable_descriptors) {
            getAnonymous<Array<CuppingDescriptor>>('cuppingforms/descriptors/')
              .then((response) => {
                setDescriptors(response.data)
              })
              .catch(() => {
                // TODO: load descriptors error
              })
          }
        })
        .catch((error) => {
          if (error.response.status === 410) {
            setLoadError(t('cupping.sessionisclosed'))
          } else {
            setLoadError(t('cupping.loadcuppingsessionerror'))
          }
          setIsLoading(false)
        })
    }
  }, [params.cuppingGuid, t])

  React.useEffect(() => {
    const beforeUnloadFn = (event: BeforeUnloadEvent) => {
      return (event.returnValue = 'Are you sure you want to exit?')
    }
    if (hasFormChanges) {
      window.addEventListener('beforeunload', beforeUnloadFn)
    }
    return () => {
      window.removeEventListener('beforeunload', beforeUnloadFn)
    }
  }, [hasFormChanges])

  React.useEffect(() => {
    if (cuppingSessionData?.url && cuppingScores.length > 0) {
      setTemporaryCuppingScores({
        evaluator_name: '',
        user: '',
        cupping_session: cuppingSessionData.url,
        sample_scores: cuppingScores,
      })
    }
  }, [cuppingScores, cuppingSessionData?.url])

  const areAllSampleFieldsFilled = (index: number): boolean => {
    return cuppingScores[index].scores.every((x) => x.score !== undefined)
  }

  const areAllFieldsFilled = (): boolean => {
    // if all scores are above 0, user has filled every field
    return cuppingScores.every((_score, index) => areAllSampleFieldsFilled(index))
  }

  const submitScores = (): void => {
    if (cuppingSessionData) {
      const saveData = {
        evaluator_name: evaluatorName,
        user: state.user?.url || null,
        cupping_session: cuppingSessionData.url,
        sample_scores: cuppingScores.map((x) => {
          return {
            ...x,
            total_score: !!cuppingSessionData.cupping_form_data.score_calculation
              ? calculateSampleScore(cuppingSessionData, x.scores)
              : null,
          }
        }),
      }
      setIsSaving(true)
      setHasSubmitError(false)
      postAnonymous<CuppingSessionResult, {}>(`cuppingsessions/${cuppingSessionData.id}/results/`, saveData)
        .then(() => {
          const completedSessions = getCompletedCuppingSessions()
          setCompletedCuppingSessions([...completedSessions, cuppingSessionData.id])
          setHasFormChanges(false)
          getAnonymous<CuppingSession>(`cuppingsessions/${params.cuppingGuid}/sessionconfig/`)
            .then((response) => {
              setCuppingSessionData(response.data)
              setIsSaving(false)
            })
            .catch(() => {
              setLoadError(t('cupping.loadresultserror'))
              setIsSaving(false)
            })
        })
        .catch(() => {
          setHasSubmitError(true)
          setIsSaving(false)
        })
    }
  }

  const isCompletedCuppingSession = () => {
    const completedSessions = getCompletedCuppingSessions()
    return cuppingSessionData ? completedSessions.indexOf(cuppingSessionData.id) > -1 : false
  }

  return (
    <Container component="main" maxWidth={false}>
      <Stack spacing={3} mt={1} alignItems="center">
        {isLoading && <CircularProgress />}
        {loadError && <Alert severity="error">{loadError}</Alert>}
        {isCompletedCuppingSession() && cuppingSessionData?.session_scores ? (
          <>
            <Alert severity="success">{t('cupping.resultssubmitted')}</Alert>
            <Results session={cuppingSessionData} />
          </>
        ) : (
          <Stack maxWidth="sm" width="100%">
            {cuppingSessionData && currentCuppingScoreIndex === SUBMIT_PAGE_INDEX && (
              <>
                <Stack spacing={1}>
                  <TextField
                    label={t('cupping.evaluatorname')}
                    value={evaluatorName}
                    onChange={(event) => setEvaluatorName(event.target.value)}
                    inputProps={{ maxLength: 200 }}
                  />
                  <LoadingButton
                    color="success"
                    loading={isSaving}
                    variant="contained"
                    disabled={!areAllFieldsFilled() || !!loadError || !evaluatorName}
                    onClick={submitScores}
                  >
                    {t('cupping.submit')}
                  </LoadingButton>
                  {!areAllFieldsFilled() && (
                    <Typography variant="caption">{t('cupping.requiredfieldwarning')}</Typography>
                  )}
                  {hasSubmitError && <Alert severity="error">{t('cupping.submiterror')}</Alert>}
                </Stack>
                <SummaryTable scores={cuppingScores} session={cuppingSessionData} />
              </>
            )}
            {cuppingSessionData && currentCuppingScoreIndex !== SUBMIT_PAGE_INDEX && (
              <CuppingScoreForm
                sampleLabel={<CuppingSampleHeader session={cuppingSessionData} index={currentCuppingScoreIndex} />}
                cuppingFormConfig={cuppingSessionData.cupping_form_data}
                availableDescriptors={descriptors}
                resultSample={cuppingScores[currentCuppingScoreIndex]}
                totalScore={calculateSampleScore(cuppingSessionData, cuppingScores[currentCuppingScoreIndex].scores)}
                onUpdateScore={(sampleId: number, fieldId: number, newScore: number) => {
                  setHasFormChanges(true)
                  setCuppingScores((oldScores) => {
                    return oldScores.map((oldScore) => {
                      return oldScore.cupping_session_sample === sampleId
                        ? {
                            ...oldScore,
                            scores: oldScore.scores.map((oldScoreValue) => {
                              return oldScoreValue.cupping_form_field === fieldId
                                ? {
                                    ...oldScoreValue,
                                    score: newScore,
                                  }
                                : oldScoreValue
                            }),
                          }
                        : oldScore
                    })
                  })
                }}
                onUpdateDescriptors={(sampleId: number, newDescriptors: Array<CuppingDescriptor>) => {
                  setHasFormChanges(true)
                  setCuppingScores((oldScores) => {
                    return oldScores.map((oldScore) => {
                      return oldScore.cupping_session_sample === sampleId
                        ? {
                            ...oldScore,
                            descriptors: newDescriptors,
                          }
                        : oldScore
                    })
                  })
                }}
                onUpdateNotes={(sampleId: number, newNotes: string) => {
                  setHasFormChanges(true)
                  setCuppingScores((oldScores) => {
                    return oldScores.map((oldScore) => {
                      return oldScore.cupping_session_sample === sampleId
                        ? {
                            ...oldScore,
                            notes: newNotes,
                          }
                        : oldScore
                    })
                  })
                }}
              />
            )}
            {cuppingSessionData && (
              <Stack
                alignItems="center"
                sx={{
                  position: 'fixed',
                  bottom: -1,
                  left: { xs: 3, md: 'auto' },
                  right: { xs: 3, md: 'auto' },
                  paddingBottom: 1,
                  paddingTop: 1,
                  zIndex: 7,
                  backgroundColor: 'background.default',
                }}
              >
                <Pagination
                  size="large"
                  shape="rounded"
                  siblingCount={isScreenBelowMdBreakpoint ? 1 : 4}
                  count={cuppingSessionData.cupping_samples.length + 1}
                  sx={
                    isScreenBelowMdBreakpoint
                      ? {
                          '.MuiPagination-ul': {
                            justifyContent: 'center',
                          },
                          '.MuiPagination-ul > li': {
                            order: 3,
                            alignSelf: 'center',
                          },
                          '.MuiPagination-ul .MuiPaginationItem-previousNext': {
                            width: '80%',
                            display: 'block',
                            marginLeft: 'auto',
                            marginRight: 'auto',
                          },
                          '.MuiPagination-ul > li:first-of-type': {
                            flexBasis: '50%',
                            order: 1,
                          },
                          ' .MuiPagination-ul > li:last-of-type': {
                            flexBasis: '50%',
                            order: 2,
                          },
                        }
                      : {}
                  }
                  renderItem={(item) => {
                    if (item.type === 'page') {
                      if (item.page === cuppingSessionData.cupping_samples.length + 1) {
                        return <PaginationItem {...item} page={<PublishOutlinedIcon />} />
                      }

                      const isFinishedSample = false //item.page !== null && areAllSampleFieldsFilled(item.page - 1)
                      const label = getCuppingSampleLabel(item.page ? item.page - 1 : 0)
                      return (
                        <Badge
                          invisible={!isFinishedSample}
                          badgeContent={<CheckIcon fontSize="small" color="success" />}
                        >
                          <PaginationItem {...item} sx={{ minWidth: '38px' }} page={label} />
                        </Badge>
                      )
                    } else {
                      return (
                        <PaginationItem
                          {...item}
                          component={React.forwardRef<HTMLButtonElement, React.PropsWithChildren<{}>>(
                            (props, ref): JSX.Element => {
                              return (
                                <Button {...props} ref={ref} variant="contained">
                                  {props.children}
                                </Button>
                              )
                            }
                          )}
                          components={{
                            next: (props) => <>Next</>,
                            previous: (props) => <>Prev</>,
                          }}
                        />
                      )
                    }
                  }}
                  onChange={(_event, page: number): void => {
                    const newPage =
                      page === cuppingSessionData.cupping_samples.length + 1 ? SUBMIT_PAGE_INDEX : page - 1
                    setCurrentCuppingScoreIndex(newPage)
                  }}
                />
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </Container>
  )
}

export default RegisterCuppingView
